// Overview — the landing page. KPI cards + smart narrative + drill-into tiles.
import { useInfoModal, PageChrome, dqBadgeFor } from './Chrome.jsx';
import { fmtN, fmtH1, narrativeForOverview, pipelineHealth, SUB_PAGES } from './helpers.js';
import { Banner, KpiCard, KpiGrid, SmartText, Sparkline, Tile, TileGrid } from './Widgets.jsx';
import { InfoModal } from './Chrome.jsx';

function subtitleFor(stats) {
  const total = stats.total || 0;
  if (total === 0) return 'No data yet — start by pulling from Dayforce.';
  const pending = stats.pending || 0;
  const gap = stats.gap || 0;
  const pq = stats.push_queue || {};
  const failedPush = pq.failed || 0;
  if (failedPush > 0) return `${failedPush} NetSuite push${failedPush === 1 ? '' : 'es'} failed — open Push Performance.`;
  if (gap > 0 && pending > 0) return `${fmtN(pending)} ready for review · ${fmtN(gap)} blocked by mapping gaps.`;
  if (gap > 0) return `${fmtN(gap)} records blocked by mapping gaps — fix on the Blockers page.`;
  if (pending > 0) return `${fmtN(pending)} records ready for review.`;
  return 'All records processed — no immediate action required.';
}

export function PageOverview({ stats, nav }) {
  const info = useInfoModal();
  const helpEntry = SUB_PAGES.find((p) => p.id === 'overview');
  const total = stats.total || 0;
  const pq = stats.push_queue || {};
  const failedPush = pq.failed || 0;
  const buckets = pipelineHealth(stats.freshness || []);
  const failedPipelines = buckets.filter((b) => b.status === 'failed');
  const openAlerts = stats.open_alerts || [];
  const trend = stats.daily_trend || [];

  return (
    <>
      <PageChrome
        title="PSC Billing — Operations"
        subtitle={subtitleFor(stats)}
        onInfo={info.onOpen}
        dqBadge={dqBadgeFor(stats, () => nav.goSubPage('data_quality'))}
      />

      {openAlerts.length > 0 && (
        <Banner tone="red" title={`${openAlerts.length} open alert${openAlerts.length === 1 ? '' : 's'}`}>
          {openAlerts[0].message || openAlerts[0].alert_type}
          {' '}
          <button type="button" className="btn btn-sm" onClick={() => nav.goAudit()}>
            Audit Log →
          </button>
        </Banner>
      )}

      {failedPipelines.length > 0 && (
        <Banner tone="red" title={`${failedPipelines.length} pipeline${failedPipelines.length === 1 ? '' : 's'} failed`}>
          {failedPipelines.map((p) => p.label).join(', ')}.{' '}
          <button type="button" className="btn btn-sm" onClick={() => nav.goSubPage('pipeline')}>
            See Pipeline Health →
          </button>
        </Banner>
      )}

      {total === 0 ? (
        <div className="db-empty">
          <div className="db-empty-title">No billing records yet</div>
          <div className="db-empty-sub">
            Once data is pulled from Dayforce, this dashboard will guide you to what needs attention.
          </div>
          <button type="button" className="btn btn-primary" onClick={() => nav.goExtraction()}>
            Go to Data Extraction →
          </button>
        </div>
      ) : (
        <>
          <KpiGrid>
            <KpiCard
              label="Ready to Bill" tone="orange"
              value={stats.billable_hours || 0} suffix="h"
              sub={`${fmtN(stats.pending || 0)} pending records`}
              cta="Sync Today" onClick={() => nav.goSubPage('push_today')}
            />
            <KpiCard
              label="Blocked Hours" tone="red"
              value={stats.gap_hours || 0} suffix="h"
              sub={`${fmtN(stats.gap || 0)} gap records`}
              cta="Fix Blockers" onClick={() => nav.goSubPage('blockers')}
            />
            <KpiCard
              label="Synced to NetSuite" tone="green"
              value={stats.pushed || 0}
              sub={failedPush > 0 ? `${failedPush} failed syncs` : 'No sync failures'}
              cta="Sync Performance" onClick={() => nav.goSubPage('push_today')}
            />
            <KpiCard
              label="Skipped by Rules" tone="gray"
              value={stats.excluded_ref_data || 0}
              sub={`${fmtH1(stats.excluded_ref_data_hours || 0)} intentionally excluded`}
              cta="Verify exclusions" onClick={() => nav.goSubPage('push_today')}
            />
          </KpiGrid>

          <SmartText lines={narrativeForOverview(stats)} />

          <div className="db-card">
            <div className="db-card-title">7-day pending volume</div>
            <Sparkline
              values={trend.slice(-7).map((d) => d.pending || 0)}
              color="var(--db-orange)"
            />
            <div className="db-card-sub" style={{ marginTop: 6 }}>
              Daily pending records, last 7 days. Open the Trends page for the full chart.
            </div>
          </div>

          <div className="db-card-title" style={{ paddingLeft: 4, marginBottom: 10 }}>
            Jump to a focus area
          </div>
          <TileGrid>
            <Tile
              tone="navy"   icon="P"
              label="Pipeline Health"
              sub={`${buckets.filter((b) => b.status === 'completed').length} of ${buckets.length} pipelines healthy.`}
              meta="Open" onClick={() => nav.goSubPage('pipeline')}
            />
            <Tile
              tone="orange" icon="$"
              label="Sync Today"
              sub="What can you approve and send to NetSuite right now?"
              meta="Open" onClick={() => nav.goSubPage('push_today')}
            />
            <Tile
              tone="red"    icon="!"
              label="Blockers"
              sub={`${fmtN(stats.gap || 0)} records can't be billed without a fix.`}
              meta="Open" onClick={() => nav.goSubPage('blockers')}
            />
            <Tile
              tone="navy"   icon="↗"
              label="Trends"
              sub="Volume, gaps, exclusions over the last 14 days."
              meta="Open" onClick={() => nav.goSubPage('trends')}
            />
            <Tile
              tone="navy"   icon="L"
              label="By Location"
              sub="Where pending and blocked records are concentrated."
              meta="Open" onClick={() => nav.goSubPage('locations')}
            />
            <Tile
              tone="orange" icon="OT"
              label="OT Watchlist"
              sub="Employees flagged for customer overtime."
              meta="Open" onClick={() => nav.goSubPage('watchlist')}
            />
            <Tile
              tone="green"  icon="✓"
              label="Data Quality"
              sub="Every rule we check, transparently scored."
              meta="Open" onClick={() => nav.goSubPage('data_quality')}
            />
          </TileGrid>
        </>
      )}

      <InfoModal open={info.open} onClose={info.onClose} title="About the Overview"
        body={helpEntry?.help} />
    </>
  );
}
